/**
 * "View as a student" preview window.
 *
 * Staff open /preview/course/:id in a NEW browser window (openLearnerPreview). Inside it the signed-in
 * instructor / admin is presented to the whole app shell as a LEARNER (role override), so the page
 * is exactly what a student sees: learner menu, learner context bar, learner course player.
 * Nothing is recorded: exam links open the exam preview, and the learner menu is display-only.
 */
import React, { useContext, useMemo } from 'react';
import AuthContext, { useAuth } from '../context/AuthContext';
import { TabsProvider } from './shell/TabsContext';
import { Eye, X } from '@phosphor-icons/react';

export const PREVIEW_PREFIX = '/preview';

export function openLearnerPreview(path) {
  const url = `${PREVIEW_PREFIX}${path.startsWith('/') ? path : `/${path}`}`;
  const w = window.open(url, 'lms-learner-preview', 'width=1440,height=900,menubar=no,toolbar=no,location=yes,resizable=yes,scrollbars=yes');
  if (w) w.focus();
  else window.location.assign(url); // popup blocked → same tab
}

export function useIsPreview() {
  const { user } = useAuth();
  return !!user?._preview;
}

export function PreviewLearnerProvider({ children }) {
  const real = useContext(AuthContext);
  const value = useMemo(() => {
    if (!real?.user) return real;
    const user = { ...real.user, role: 'learner', _preview: true, _realRole: real.user.role };
    return { ...real, user, isSuperAdmin: false, isTenantAdmin: false, isInstructor: false, isLearner: true };
  }, [real]);
  return (
    <AuthContext.Provider value={value}>
      <TabsProvider>
        <div className="h-screen flex flex-col">
          <div className="h-8 flex-shrink-0 bg-amber-400 text-amber-950 text-[12px] font-medium flex items-center gap-2 px-3" data-testid="preview-banner">
            <Eye className="w-4 h-4" weight="fill" />
            <span>Preview — you are seeing this course exactly as a student does. Nothing you do here is recorded.</span>
            <button type="button" onClick={() => { if (window.opener) window.close(); else window.history.back(); }} className="ml-auto inline-flex items-center gap-1 rounded px-2 h-6 bg-amber-950/10 hover:bg-amber-950/20" data-testid="close-preview">
              <X className="w-3.5 h-3.5" /> Close preview
            </button>
          </div>
          <div className="flex-1 min-h-0 preview-frame">{children}</div>
        </div>
      </TabsProvider>
    </AuthContext.Provider>
  );
}
